/**
 * Shared chrome for the authentication pages (login, register, password reset,
 * OAuth callback, 404).
 *
 * These six screens sit between the landing and the workspace, so they were the
 * most visible break left in the product: a visitor went from the brand field to
 * a pale-blue card and back. They are deliberately implemented once here rather
 * than six times — an auth flow that drifts panel by panel reads as broken, not
 * as variety.
 *
 * Brand v4 gives this screen its one illustration and one aura: the auth moment
 * is exactly the kind of beat that earns them, and nothing else on it does. The
 * illustration sits beside the panel on desktop and is dropped entirely below
 * `md` rather than shrunk — on a phone the form is the whole screen.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { isRecaptchaEnabled } from '@/lib/recaptcha';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useOsRoot, useTheme } from '@/lib/theme';
import { AikkaLogo } from '@os/components/AikkaLogo';
import { PublicLanguageSwitcher } from '@os/components/PublicLanguageSwitcher';

interface AuthShellProps {
  title: string;
  subtitle?: string;
  /** Rendered above the form — used for the OAuth block. */
  aside?: ReactNode;
  children: ReactNode;
  /** Bottom line, e.g. "No account? Sign up". */
  footer?: ReactNode;
  /** Brand plate behind the panel. Off for the terse screens (callback, 404). */
  shape?: boolean;
  /**
   * Which of the brand illustrations sits beside the panel. Both sign-in and
   * registration use the soft ember `bloom` so the two auth screens read as one
   * palette (the cluster form is a heavier dark-brown sphere set, off the ember
   * aura, so it is no longer used on the entry flow). One per screen, never two.
   */
  illustration?: 'bloom' | 'cluster';
}

const ILLUSTRATIONS = {
  bloom: { src: '/img/brand/ill_bloom.webp', width: 720, height: 688 },
  cluster: { src: '/img/brand/ill_cluster.webp', width: 374, height: 405 },
} as const;

export function AuthShell({
  title,
  subtitle,
  aside,
  children,
  footer,
  shape = true,
  illustration = 'bloom',
}: AuthShellProps) {
  const illus = ILLUSTRATIONS[illustration];
  // Le sol du document (`html.os-root`). Cet écran ne le revendiquait PAS : il
  // vivait du seul script d'`index.html`, qui le pose avant l'hydratation sur
  // les chemins produit. Or les surfaces publiques le RETIRAIENT à leur
  // démontage, donc `/store → /login` en sombre (navigation côté client)
  // laissait un `/login` noir sur un `body` crème — mesuré dans Chromium sur le
  // build de production. Depuis, la classe est comptée (`useOsRoot`) et cet
  // écran la revendique comme les quatre autres : c'était la seule porte du
  // produit qui n'avait pas de sol à elle.
  useOsRoot();
  const { isDark } = useTheme();
  return (
    <div className="os-scope grain relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-5 py-12">
      {/* DA v4 exacte : le même halo radial rotatif que le hero du landing, en
          fond ambient de l'écran d'auth. Sous le contenu (z-0), clippé par
          overflow-hidden ; le voile de gauche (plus bas) garde le type lisible. */}
      <div className="hero-bloom" aria-hidden="true" />
      {shape && (
        <>
          {/* Aura wash, bleeding off the right edge — never boxed, never behind
              the form. */}
          <div
            className="brand-shape absolute -right-[22%] top-1/2 hidden h-[78vh] w-[78vh] -translate-y-1/2 opacity-80 drift-slow md:block"
            style={{ backgroundImage: 'url(/img/brand/aura_ember.webp)' }}
          />
          {/* One illustration, one screen. Hidden under lg so it can never
              crowd the form. */}
          {/* LA FLEUR, EN HAUT À GAUCHE ET PLUS GRANDE.
              Le client : « l'image de la fleur, la bien visible, mais plutôt
              mid gauche haut ». Elle était collée au bord droit, à mi-hauteur,
              c'est-à-dire dans le seul quart de l'écran que le voile laissait
              respirer — donc à la fois excentrée et en concurrence avec l'aura
              qui occupe déjà ce côté. En haut à gauche elle prend le vide
              au-dessus du logo, et le formulaire garde son axe.

              `w` monte de 30vw/340 à 34vw/400 : « bien visible » veut dire
              qu'on la lit comme une illustration, pas comme une texture.
              `lg:block` ne bouge pas — sous ce palier elle tomberait sur le
              formulaire, qui est ce que la page sert. */}
          <img
            src={illus.src}
            alt=""
            aria-hidden="true"
            width={illus.width}
            height={illus.height}
            className="illus absolute left-[5%] top-[11%] hidden w-[min(34vw,400px)] opacity-95 lg:block"
          />
        </>
      )}
      {/* Keep the type on clean ground, never on the wash. The veil IS the page
          ground, so the left half reads as brand ground rather than a cold grey
          seam, then fades out to let the bloom breathe on the right.

          Il était écrit `#F5F4EF` en dur — le papier chaud, à l'octet près, ce
          qui était juste tant que cet écran ne pouvait être que clair. Le lot 3
          rend `AuthShell` sombre (docs/dark-theme-scope.md §6 : la porte du
          produit) et l'aplat câblé devenait alors une bande CRÈME sur le tiers
          gauche d'une page noire — vérifié en capture, pas déduit. Il lit
          maintenant `--background` : même valeur exacte en clair, l'encre en
          sombre. En style en ligne et non en classe Tailwind arbitraire, parce
          que la barre d'alpha de `hsl(… / 0.72)` s'y confondrait avec le
          modificateur d'opacité. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          /* LE VOILE SE RECENTRE SUR LE FORMULAIRE.
             Il était un dégradé horizontal opaque sur les 42 % de GAUCHE : une
             fleur posée là aurait été purement et simplement effacée. Ce qu'il
             protège n'est d'ailleurs pas « la gauche », c'est la colonne du
             formulaire — le logo au-dessus et la ligne « pas de compte ? » en
             dessous, qui sont les deux seuls textes posés à même le fond (la
             carte, elle, a sa propre plaque).

             Une ellipse centrée fait donc le même travail sur moins de surface,
             et laisse les deux illustrations respirer de part et d'autre : la
             fleur en haut à gauche, l'aura au bord droit. */
          background:
            'radial-gradient(ellipse 46% 72% at 50% 50%, hsl(var(--background)) 0%,' +
            ' hsl(var(--background)) 52%, hsl(var(--background) / 0.7) 76%,' +
            ' hsl(var(--background) / 0) 100%)',
        }}
      />

      {/* Language selector — a logged-out visitor can switch the interface
          language on the auth screens too (claude.ai parity). */}
      <div className="absolute right-5 top-5 z-20">
        {/* LE CONTRÔLE SUIT LE THÈME, ET NE POSE AUCUN FOND.
            Le client : « en light et en dark je veux que le fond soit sans
            opacité et juste lisible selon le thème — écris en noir ou gris en
            light, blanc ou crème en dark. Idem pour le choix dans le sous-menu.
            À faire seulement sur les pages register / login. »

            DEUX FOIS LE MÊME DÉFAUT, ET IL ÉTAIT MESURABLE. `ink-solid` vaut
            `bg-ink text-white` et `MENU_VARIANT['on-ink']` vaut `bg-ink` : or
            `--brand-ink` s'INVERSE en blanc sous le thème sombre (os.css §7).
            Sur l'écran noir, le déclencheur sortait en fond blanc / texte
            blanc, et le menu ouvert en pavé blanc opaque — deux rectangles
            vides. Ce qui manquait n'était pas une teinte, c'était de cesser de
            peindre un fond avec un jeton qui change de camp.

            `on-light` / `on-ink` sont les deux variantes SANS sol : elles ne
            portent que la couleur du type — `#2f2c28` sur le papier, crème sur
            l'encre. `menuPlate="bg-transparent"` étend la même règle au menu,
            bordure et ombre comprises, et bascule ses rangées sur le jeu fondu
            (l'actif se lit à la graisse, plus à un pavé).

            Cette page est la seule concernée : `AuthShell` ne sert que la
            famille d'authentification — login, register, mot de passe oublié,
            réinitialisation, vérification d'e-mail, retour OAuth. Le contrôle
            ne doit pas changer d'allure entre « Se connecter » et « Mot de
            passe oublié », qui sont deux pas du même geste. */}
        <PublicLanguageSwitcher
          variant={isDark ? 'on-ink' : 'on-light'}
          menuPlate="bg-transparent"
        />
      </div>

      <div className="relative z-10 w-full max-w-[420px] fade-up">
        <Link to="/" className="mb-8 inline-flex" aria-label="aikka — home">
          <AikkaLogo />
        </Link>

        {/* Warm glass lift: the brand expresses structure with hairlines, so the
            shadow stays a soft, warm (plum #403233) haze — enough to float the
            card above the paper without reading as a drop-shadow UI. */}
        <div className="panel corner-ticks p-7 shadow-[0_1px_1px_rgba(64,50,51,0.03),0_18px_50px_-22px_rgba(64,50,51,0.20)] sm:p-8">
          <h1 className="font-display text-[2rem] leading-[1.05]">{title}</h1>
          {subtitle && <p className="mt-2 text-[0.9rem] leading-relaxed text-ink-2">{subtitle}</p>}

          {aside && <div className="mt-7">{aside}</div>}
          <div className="mt-6">{children}</div>
        </div>

        {footer && <div className="mt-5 text-center text-[0.85rem] text-ink-2">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Error banner. Ember is the brand's loudest colour, which is exactly what an
 * authentication failure should borrow — a red that exists nowhere else in the
 * system would read as a different product. The label is ink, not the accent:
 * accent-on-tint at 13px would not clear 4.5:1.
 */
export function AuthError({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="mb-4 rounded-control border border-signal/50 bg-signal/10 px-3.5 py-2.5 text-[0.85rem] text-foreground"
    >
      {children}
    </div>
  );
}

interface AuthFieldProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  /** Rendered opposite the label, e.g. a "Forgot?" link. */
  action?: ReactNode;
  hint?: string;
  /**
   * Pour un champ mot de passe : un bouton qui l'affiche en clair. Réservé aux
   * mots de passe qu'on CRÉE (inscription, réinitialisation) — c'est là qu'une
   * faute de frappe invisible coûte un compte qu'on ne sait plus ouvrir.
   */
  revealLabels?: { show: string; hide: string };
  /** L'id de ce qui décrit le champ (critères, jauge), relié par `aria-describedby`. */
  describedBy?: string;
}

export function AuthField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  autoComplete,
  action,
  hint,
  revealLabels,
  describedBy,
}: AuthFieldProps) {
  const [revealed, setRevealed] = useState(false);
  const inputType = revealLabels && revealed ? 'text' : type;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        {/* `htmlFor`/`id` and the label text are load-bearing: the e2e suite
            selects these fields with getByLabel(). Do not rename. */}
        <label htmlFor={id} className="mono-label !text-ink-2">
          {label}
        </label>
        {action}
      </div>
      <div className="relative">
        <input
          id={id}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
          aria-describedby={describedBy}
          className={`h-11 w-full rounded-control border border-border bg-surface-2 px-3.5 text-[0.9rem] text-foreground placeholder:text-ink-3 transition-colors focus:border-signal/60 focus:outline-none${revealLabels ? ' pr-11' : ''}`}
        />
        {revealLabels && (
          // Hors du <label> et nommé par `aria-label` : `getByLabel('Mot de
          // passe')` des e2e vise l'input par son <label>, le bouton porte
          // « Afficher le mot de passe » — Playwright en correspondance exacte
          // ne les confond pas.
          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            aria-label={revealed ? revealLabels.hide : revealLabels.show}
            aria-pressed={revealed}
            aria-controls={id}
            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-control text-ink-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-signal"
          >
            {revealed ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
          </button>
        )}
      </div>
      {hint && <p className="text-[0.75rem] text-ink-2">{hint}</p>}
    </div>
  );
}

export function AuthSubmit({
  loading,
  children,
  loadingLabel,
}: {
  loading?: boolean;
  children: ReactNode;
  loadingLabel?: string;
}) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="btn btn--primary h-11 w-full text-[0.9rem] disabled:cursor-not-allowed"
    >
      {loading ? (loadingLabel ?? children) : children}
    </button>
  );
}

export function AuthDivider({ label }: { label: string }) {
  return (
    <div className="my-6 flex items-center gap-4">
      <div className="hairline h-px flex-1" />
      <span className="mono-label">{label}</span>
      <div className="hairline h-px flex-1" />
    </div>
  );
}

/** Inline link in the brand's single accent. */
/**
 * La classe d'UN lien d'authentification. Une seule, lue par les deux formes.
 *
 * EXPORTÉE parce qu'elle était recopiée ailleurs, et qu'une chaîne recopiée n'est
 * plus une classe unique : `ConfirmEmailGate` la retapait mot pour mot, donc
 * l'opacité de soulignement qu'elle porte — un écart à la planche 21, gelé ici au
 * registre de `check-opacity-scale.mjs` — se dupliquait avec elle et faisait
 * monter un compteur qui ne doit que baisser. L'importer rend le même pixel et
 * ne compte qu'une fois. Ne pas réécrire la classe dans ce commentaire : le
 * scanner lit le fichier brut, commentaires compris.
 */
export const AUTH_LINK_CLASS =
  'text-signal-ink underline decoration-signal/40 underline-offset-4 hover:decoration-signal';

export function AuthLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className={AUTH_LINK_CLASS}>
      {children}
    </Link>
  );
}

/**
 * « Retour à la connexion » — le lien de pied des écrans « mot de passe
 * oublié » et « nouveau mot de passe ».
 *
 * IL EXISTE POUR FERMER UN ÉCART DE COULEUR. Les deux écrans l'écrivaient à la
 * main en `text-ink-2` (gris), pendant que le pied de l'écran de connexion
 * classique rendait « Se connecter » via {@link AuthLink}, en `signal-ink`
 * souligné. Même destination, deux couleurs, sur deux écrans qui se suivent
 * dans le même parcours — le client l'a relevé (Notion → Onboarding → « Retour
 * à la connexion » : « aligner les couleurs … entre le sign in classique et le
 * reset password »).
 *
 * Résolu dans le sens du lien, pas du gris : c'est une DESTINATION, et les
 * destinations de ce parcours se lisent en signal.
 */
export function AuthBackToSignIn({ children }: { children: ReactNode }) {
  return (
    <Link to="/login" className={`inline-flex items-center justify-center gap-2 ${AUTH_LINK_CLASS}`}>
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
      {children}
    </Link>
  );
}

export type AuthFormSubmit = (e: FormEvent) => void;

/**
 * La mention que Google exige quand le badge reCAPTCHA est masqué (voir
 * `hideBadge` dans src/lib/recaptcha.ts). Rien tant que reCAPTCHA est inactif :
 * annoncer une protection qui ne tourne pas serait faux.
 */
export function AuthRecaptchaNotice() {
  const { t } = useTranslation('auth');
  if (!isRecaptchaEnabled()) return null;
  return (
    <p className="text-[0.7rem] leading-relaxed text-ink-2">
      <Trans
        t={t}
        i18nKey="captcha.notice"
        components={{
          privacy: (
            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className={AUTH_LINK_CLASS} />
          ),
          terms: (
            <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className={AUTH_LINK_CLASS} />
          ),
        }}
      />
    </p>
  );
}
